/* ====================================================================
   My pets - the controller for sections/pp-pet-profiles.liquid.

   Three things happen here and nothing else should:

     1. the pet switcher is built once and never torn down, so the
        active ring can actually travel to the new pet rather than
        being destroyed and rebuilt in place;
     2. the hero and the detail column are swapped, with the switcher
        left alone - that separation is the whole reason a clean
        transition is possible;
     3. facets are derived from the profile and each one is removable,
        which re-fetches the shelf.

   The shelf is a fetch of templates/collection.pet-shelf.liquid, so the
   products, prices and add-to-cart forms are real Liquid rather than
   anything reconstructed in JavaScript.

   Storage goes through window.PetPlusPetStore. Do not read localStorage
   here.
   ==================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-pp-pets]');
  if (!root || !window.PetPlusPetStore) return;

  var Store = window.PetPlusPetStore;
  var CONFIG = readJSON('[data-pp-config]', { species: {}, shelfCount: 8, shelfView: 'pet-shelf' });
  var ORDERS = readJSON('[data-pp-orders]', []);
  var SPECIES = CONFIG.species || {};
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var EASE_OUT = 'cubic-bezier(.4,0,1,1)';
  var EASE_IN = 'cubic-bezier(.32,.72,0,1)';
  var MAX_SOURCE_PHOTO_BYTES = 10 * 1024 * 1024;
  var MAX_UPLOAD_PHOTO_BYTES = 700 * 1024;
  var PHOTO_SIZE = 512;
  var MAX_VERIFICATION_BYTES = 5 * 1024 * 1024;

  /* Lives inside the passport card, which is rebuilt on every paint - so
     it is looked up each time rather than captured at startup. */
  function switchBox() { return detail.querySelector('[data-pp-switch]'); }
  var detail = root.querySelector('[data-pp-detail]');

  var pets = [];
  var focusId = null;
  var off = {};          /* facet key -> true when the shopper switched it off */
  /* Survives a repaint: toggling a chip re-renders the panel, and without
     this the block someone just opened would shut under their hand. */
  var matchOpen = false;
  var swapping = false;
  var shelfToken = 0;    /* guards against a slow fetch landing after a newer one */

  function readJSON(sel, fallback) {
    var el = root.querySelector(sel);
    if (!el) return fallback;
    try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
  }

  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function handle(v) {
    return String(v || '').toLowerCase().trim()
      .replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function titled(v) {
    v = String(v || '');
    return v.charAt(0).toUpperCase() + v.slice(1);
  }

  function photoFor(p, fallback) {
    return Store.photoUrl ? Store.photoUrl(p, fallback) : fallback;
  }

  /* ---------- what we know about the animal ------------------------- */

  function ageYears(p) {
    if (p.birthday) {
      var b = new Date(p.birthday);
      if (!isNaN(b.getTime())) return (Date.now() - b.getTime()) / 31557600000;
    }
    /* Older profiles from the account page carry age + age_unit instead. */
    var a = parseFloat(p.age || 0);
    if (!a) return null;
    return p.age_unit === 'months' ? a / 12 : a;
  }

  function ageLabel(p) {
    var a = ageYears(p);
    if (a === null) return '';
    if (a < 1) return Math.max(1, Math.round(a * 12)) + ' months old';
    var y = Math.floor(a);
    return y + (y === 1 ? ' year old' : ' years old');
  }

  function stageOf(p) {
    var a = ageYears(p);
    if (a === null) return '';
    var pc = p.pet_type === 'dogs' || p.pet_type === 'cats';
    if (pc && a < 1) return p.pet_type === 'dogs' ? 'puppy' : 'kitten';
    if (pc && a >= 7) return 'senior';
    return 'adult';
  }

  function nextBirthday(p) {
    if (!p.birthday) return null;
    var b = new Date(p.birthday);
    if (isNaN(b.getTime())) return null;
    var now = new Date();
    var next = new Date(now.getFullYear(), b.getMonth(), b.getDate());
    if (next < now) next.setFullYear(now.getFullYear() + 1);
    return {
      days: Math.round((next - now) / 86400000),
      turning: next.getFullYear() - b.getFullYear(),
      day: next.getDate(),
      mon: next.toLocaleDateString(undefined, { month: 'short' }).toUpperCase(),
      label: next.toLocaleDateString(undefined, { day: 'numeric', month: 'long' })
    };
  }

  /* Facets are the profile, restated as the tags the collections use.
     Each one is a chip the shopper can switch off. */
  function facetsFor(p) {
    var f = [];
    var stage = stageOf(p);
    if (stage) f.push({ k: 'lifestage_' + stage, label: titled(stage) });
    if (p.pet_type === 'dogs' && p.pet_size) {
      f.push({ k: 'breed-size_' + handle(p.pet_size), label: titled(p.pet_size) + ' breed' });
    }
    if (p.preferred_protein) f.push({ k: 'protein_' + handle(p.preferred_protein), label: p.preferred_protein });
    if (p.food_form) f.push({ k: 'form_' + handle(p.food_form), label: p.food_form + ' food' });
    (p.health_goals || []).forEach(function (g) {
      f.push({ k: 'special-condition_' + handle(g), label: g });
    });
    if (p.activity_level === 'High') {
      f.push({ k: 'special-condition_working-and-endurance', label: 'Very active' });
    }
    /* Exclusions cannot be expressed in a tag URL, so they are applied to
       the returned markup instead. They still show as chips. */
    (p.avoid_proteins || []).forEach(function (a) {
      f.push({ k: 'protein_' + handle(a), label: 'No ' + a.toLowerCase(), avoid: true });
    });
    return f;
  }

  function activeFacets(p) {
    return facetsFor(p).filter(function (f) { return !off[f.k]; });
  }

  /* ---------- rendering -------------------------------------------- */

  function speciesOf(p) { return SPECIES[p.pet_type] || null; }

  function render() {
    pets = pets.filter(function (p) { return SPECIES[p.pet_type]; });
    if (!pets.length) {
      root.setAttribute('data-pp-state', 'empty');
      return;
    }
    if (!pets.some(function (p) { return p.id === focusId; })) focusId = pets[0].id;
    root.setAttribute('data-pp-state', 'ready');
    paint();
  }

  function renderSwitcher() {
    var switcher = switchBox();
    if (!switcher) return;
    switcher.innerHTML = pets.map(function (p) {
      var s = speciesOf(p);
      return '<button class="ppp-pet" type="button" data-pp-focus="' + esc(p.id) + '"' +
        ' aria-pressed="' + (p.id === focusId) + '">' +
        '<span class="ppp-pet__disc"><img src="' + esc(photoFor(p, s.face)) + '" alt="" width="320" height="320" loading="lazy"></span>' +
        '<span class="ppp-pet__name">' + esc(p.pet_name) + '</span></button>';
    }).join('') +
      '<button class="ppp-pet ppp-pet--add" type="button" data-pp-new>' +
      '<span class="ppp-pet__disc" aria-hidden="true">+</span>' +
      '<span class="ppp-pet__name">Add</span></button>';

    switcher.querySelectorAll('[data-pp-focus]').forEach(function (el) {
      el.addEventListener('click', function () { switchPet(el.getAttribute('data-pp-focus')); });
    });
    switcher.querySelectorAll('[data-pp-new]').forEach(function (el) {
      el.addEventListener('click', function () { openWizard(null); });
    });
  }

  function markActive(id) {
    var switcher = switchBox();
    if (!switcher) return;
    switcher.querySelectorAll('[data-pp-focus]').forEach(function (el) {
      el.setAttribute('aria-pressed', String(el.getAttribute('data-pp-focus') === id));
    });
  }

  function current() {
    return pets.filter(function (p) { return p.id === focusId; })[0];
  }

  function paint() {
    var p = current();
    if (!p) return;
    detail.innerHTML = detailHTML(p, speciesOf(p));
    renderSwitcher();
    wire();
    loadShelf(p, speciesOf(p));
  }

  /*
    The identity block, at the top of the left page. This was the full-bleed
    hero band; it is the head of the passport now, which is why the portrait
    is a small circular face rather than a 1600px photograph.

    It also means the largest thing on the page is a heading rather than an
    image, so there is nothing heavy on the critical path here any more.
  */
  function identityHTML(p, s) {
    var stage = stageOf(p);
    var meta = [stage ? titled(stage) : '',
      p.pet_size ? titled(p.pet_size) + ' breed' : '',
      p.activity_level === 'High' ? 'Very active' : '']
      .filter(Boolean)
      .map(function (m) { return '<span class="ppp-chip">' + esc(m) + '</span>'; }).join('');

    /* One line of identity, the way a passport states it. */
    var line = [p.breed, s.label || 'Pet',
      [p.sex, p.desexed ? 'neutered' : ''].filter(Boolean).join(', ')]
      .filter(Boolean).join(' &middot; ');

    return '<div class="ppp-id">' +
      '<div class="ppp-id__face" style="background:' + esc(s.ground || '#c8e0fa') + '">' +
        '<img src="' + esc(photoFor(p, s.face || s.hero)) + '" alt="" width="240" height="240" decoding="async">' +
      '</div>' +
      '<div class="ppp-id__copy">' +
        '<h1 class="ppp-id__n">' + esc(p.pet_name) + '</h1>' +
        '<p class="ppp-id__line">' + line + '</p>' +
        (meta ? '<div class="ppp-id__meta">' + meta + '</div>' : '') +
      '</div>' +
    '</div>';
  }

  var LOCK = '<svg class="ppp-ico" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">' +
    '<path d="M8 1.2a3.1 3.1 0 0 0-3.1 3.1V6H4.2A1.2 1.2 0 0 0 3 7.2v5.6A1.2 1.2 0 0 0 4.2 14h7.6a1.2 1.2 0 0 0 1.2-1.2V7.2A1.2 1.2 0 0 0 11.8 6h-.7V4.3A3.1 3.1 0 0 0 8 1.2Zm0 1.5a1.6 1.6 0 0 1 1.6 1.6V6H6.4V4.3A1.6 1.6 0 0 1 8 2.7Z"/></svg>';

  /* ==================================================================
     Parasite treatments.

     Two kinds, tick & flea and deworming. The owner logs what they gave
     and when; we work out when the same thing is next due and show it.

     What this deliberately does NOT do: suggest a dose, an interval, or a
     product. `interval_days` comes off the pack the owner chose, from a
     neutral list of repeats - it is a unit picker, not a recommendation.
     How often an animal needs worming depends on the product, the animal
     and where it lives, and that is a conversation with a vet, not
     something a shop's website should answer.

     It is also not a subscription. Nothing auto-orders; "buy again" is a
     link to the same product, and the owner clicks it or does not.
     ================================================================== */
  var TREAT_KINDS = [
    { k: 'tick_flea', label: 'Tick &amp; flea', short: 'TICK' },
    { k: 'deworm', label: 'Deworming', short: 'WORM' }
  ];

  var REPEATS = [
    { v: 7, l: '1 week' },
    { v: 14, l: '2 weeks' },
    { v: 28, l: '4 weeks' },
    { v: 30, l: '1 month' },
    { v: 90, l: '3 months' },
    { v: 180, l: '6 months' },
    { v: 365, l: '1 year' }
  ];

  var MONTH_SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

  function treatmentsOf(p) {
    return Array.isArray(p.treatments) ? p.treatments : [];
  }

  /* The most recent dose of one kind. Sorting by date rather than trusting
     insertion order, because a shopper can log a dose they forgot. */
  function lastTreatment(p, kind) {
    return treatmentsOf(p)
      .filter(function (t) { return t && t.kind === kind && t.date; })
      .sort(function (a, b) { return a.date < b.date ? 1 : -1; })[0] || null;
  }

  function midnight(d) {
    var x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  function dueInfo(t) {
    if (!t) return null;
    var given = midnight(new Date(t.date + 'T00:00:00'));
    if (isNaN(given.getTime())) return null;
    var due = new Date(given);
    due.setDate(due.getDate() + (Number(t.interval_days) || 90));
    var days = Math.round((due - midnight(new Date())) / 86400000);
    return {
      due: due,
      days: days,
      state: days < 0 ? 'overdue' : (days <= 14 ? 'soon' : 'ok'),
      label: due.getDate() + ' ' + MONTH_SHORT[due.getMonth()].charAt(0) +
        MONTH_SHORT[due.getMonth()].slice(1).toLowerCase() + ' ' + due.getFullYear()
    };
  }

  /* Drives the badge on the tab. A reminder you only see after opening the
     tab it lives in is not a reminder, so this number rides on the tab. */
  function medsDue(p) {
    return TREAT_KINDS.filter(function (kind) {
      var info = dueInfo(lastTreatment(p, kind.k));
      return info && info.state !== 'ok';
    }).length;
  }

  function medCardHTML(p, kind) {
    var last = lastTreatment(p, kind.k);
    var info = dueInfo(last);
    var shape = kind.k === 'tick_flea' ? ' ppp-med__shape--round' : ' ppp-med__shape--square';

    if (!info) {
      return '<article class="ppp-med ppp-med--empty">' +
        '<span class="ppp-med__shape' + shape + '" aria-hidden="true"></span>' +
        '<div class="ppp-med__copy"><p class="ppp-card__k">' + kind.label + '</p>' +
        '<h3>Nothing logged</h3>' +
        '<p class="ppp-card__body">Add the last treatment to place the next date.</p></div>' +
        '<button class="ppp-chip ppp-med__add" type="button" data-pp-med-add="' + kind.k + '">' +
          '<span aria-hidden="true">+</span> Add treatment</button>' +
        '</article>';
    }

    var status = info.state === 'overdue'
      ? 'OVERDUE'
      : (info.days === 0 ? 'DUE TODAY' : 'IN ' + info.days + ' DAY' + (info.days === 1 ? '' : 'S'));

    return '<article class="ppp-med ppp-med--' + info.state + '">' +
      '<span class="ppp-med__shape' + shape + '" aria-hidden="true"></span>' +
      '<div class="ppp-med__copy"><div class="ppp-med__top">' +
        '<p class="ppp-card__k">' + kind.label + '</p>' +
        '<span class="ppp-med__status">' + status + '</span></div>' +
        '<h3>' + (info.state === 'overdue' ? 'Was due ' : 'Due ') + esc(info.label) + '</h3>' +
        '<p class="ppp-card__body">Last given ' + esc(prettyDate(last.date)) +
          (last.product ? ' &middot; ' + esc(last.product) : '') + '</p></div>' +
      '<div class="ppp-med__acts">' +
        '<button class="ppp-chip ppp-chip--solid" type="button" data-pp-med-done="' + kind.k + '">Mark done today</button>' +
        '<button class="ppp-chip" type="button" data-pp-med-add="' + kind.k + '">Log another</button>' +
      '</div></article>';
  }

  function verificationState(p) {
    var status = p.verification_status || 'required';
    if (status === 'approved') {
      return { cls: 'approved', label: 'Identity approved', copy: 'Pet Plus has approved this passport.' };
    }
    if (status === 'pending') {
      return { cls: 'pending', label: 'Awaiting review', copy: 'Your identification has been sent to Pet Plus.' };
    }
    if (status === 'rejected') {
      return { cls: 'rejected', label: 'New ID needed', copy: 'Upload a new vaccination record or veterinary report.' };
    }
    return { cls: 'required', label: 'ID required', copy: 'Add a vaccination record or veterinary report to verify this passport.' };
  }

  function prettyDate(iso) {
    var d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return iso;
    var m = MONTH_SHORT[d.getMonth()];
    return d.getDate() + ' ' + m.charAt(0) + m.slice(1).toLowerCase() + ' ' + d.getFullYear();
  }

  /* A row per treatment keeps the two schedules distinct. Overdue items
     are pinned into the current month so they cannot disappear off the
     front of the six-month view. */
  function calendarHTML(p) {
    var now = midnight(new Date());
    var months = [];

    for (var i = 0; i < 6; i++) {
      var month = new Date(now.getFullYear(), now.getMonth() + i, 1);
      months.push(month);
    }

    var head = months.map(function (month, i) {
      return '<span class="ppp-cal__month' + (i === 0 ? ' is-current' : '') + '">' +
        MONTH_SHORT[month.getMonth()] + '</span>';
    }).join('');

    var rows = TREAT_KINDS.map(function (kind) {
      var info = dueInfo(lastTreatment(p, kind.k));
      var shape = kind.k === 'tick_flea' ? ' ppp-cal__shape--round' : ' ppp-cal__shape--square';
      var cells = months.map(function (month, i) {
        var mark = '';
        if (info) {
        var inThis = info.due.getFullYear() === month.getFullYear() &&
          info.due.getMonth() === month.getMonth();
        var overdueHere = i === 0 && info.state === 'overdue';
        if (inThis || overdueHere) {
            var state = overdueHere ? 'overdue' : info.state;
            mark = '<span class="ppp-cal__mark ppp-cal__mark--' + state + '" aria-label="' +
              kind.label + (overdueHere ? ' overdue' : ' due ' + esc(info.label)) + '">' +
              '<b>' + (overdueHere ? 'Late' : info.due.getDate()) + '</b>' +
              '<small>' + (overdueHere ? esc(info.label) : 'due') + '</small></span>';
          }
        }
        return '<span class="ppp-cal__cell"><i aria-hidden="true"></i>' + mark + '</span>';
      }).join('');

      return '<div class="ppp-cal__row">' +
        '<span class="ppp-cal__label"><i class="ppp-cal__shape' + shape + '" aria-hidden="true"></i>' +
          kind.label + '</span>' + cells + '</div>';
    }).join('');

    return '<div class="ppp-cal" role="region" aria-label="Next six months" tabindex="0">' +
      '<div class="ppp-cal__table"><div class="ppp-cal__head"><span>Treatment</span>' + head + '</div>' +
      rows + '</div></div>';
  }

  function medsHTML(p) {
    var tracked = TREAT_KINDS.filter(function (kind) {
      return !!lastTreatment(p, kind.k);
    }).length;
    var empty = tracked === 0;
    var headAction = empty
      ? '<div class="ppp-meds__setup"><b>Setting up first record</b><span>Nothing has been added yet</span></div>'
      : '<button class="ppp-chip ppp-chip--solid" type="button" data-pp-med-add="">+ Log treatment</button>';

    return '<div class="ppp-meds">' +
      '<header class="ppp-meds__head"><div><p class="ppp-eyebrow">Six-month view</p>' +
        '<h2>' + esc(p.pet_name) + '&rsquo;s medicine calendar</h2>' +
        '<p>Log what was given, then choose exactly when the next treatment is due.</p></div>' +
        headAction + '</header>' +
      '<form class="ppp-medform" data-pp-med-form' + (empty ? '' : ' hidden') + '>' +
        '<div class="ppp-medform__intro"><span class="ppp-medform__icon" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" fill="none"><rect x="4" y="6" width="16" height="14" rx="3" stroke="currentColor" stroke-width="1.8"/>' +
          '<path d="M8 3v6m8-6v6M4 11h16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
          '<path d="m9 15 2 2 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
          '<div><p class="ppp-card__k">New record</p><h3>Add last treatment</h3>' +
          '<p>Use the pack or your vet&rsquo;s plan.</p></div></div>' +
        '<div class="ppp-medform__body">' +
          '<label class="ppp-field"><span>Treatment</span>' +
            '<select data-pp-med-kind required>' + TREAT_KINDS.map(function (k) {
              return '<option value="' + k.k + '">' + k.label + '</option>';
            }).join('') + '</select></label>' +
          '<label class="ppp-field"><span>Date given</span>' +
            '<input type="date" data-pp-med-date required max="' + new Date().toISOString().slice(0, 10) + '"></label>' +
          '<label class="ppp-field"><span>Next treatment in</span>' +
            '<select data-pp-med-interval required><option value="" selected disabled>Choose interval</option>' +
              REPEATS.map(function (r) { return '<option value="' + r.v + '">' + r.l + '</option>'; }).join('') +
            '</select><small>As the pack or your vet says</small></label>' +
          '<div class="ppp-medform__actions"><button class="ppp-btn ppp-btn--blue ppp-medform__submit" type="submit" data-pp-med-submit disabled>Add to calendar</button>' +
            '<button class="ppp-link" type="button" data-pp-med-cancel>Cancel</button></div>' +
          '<details class="ppp-medform__optional"><summary>+ Add product name <span>Optional</span></summary>' +
            '<label class="ppp-field"><span>Product name</span>' +
            '<input type="text" data-pp-med-product maxlength="80" placeholder="e.g. Bravecto 20–40 kg"></label></details>' +
          '<p class="ppp-medform__error" data-pp-med-error role="alert" hidden>Choose the date given and time until the next treatment.</p>' +
        '</div>' +
      '</form>' +
      calendarHTML(p) +
      '<div class="ppp-meds__grid">' + TREAT_KINDS.map(function (k) { return medCardHTML(p, k); }).join('') + '</div>' +
      '<p class="ppp-meds__note">Dates come from what you log and the pack you used. ' +
      'Your vet&rsquo;s schedule always wins.</p>' +
      '</div>';
  }

  function detailHTML(p, s) {
    var bd = nextBirthday(p);
    var facets = facetsFor(p);
    var on = facets.filter(function (f) { return !off[f.k]; }).length;

    var birthday = bd
      ? '<div class="ppp-card ppp-card--birthday">' +
          '<div class="ppp-card__date" aria-hidden="true"><b>' + bd.day + '</b><span>' + esc(bd.mon) + '</span></div>' +
          '<div><p class="ppp-card__k">Coming up</p>' +
          '<h3>' + esc(p.pet_name) + ' turns ' + bd.turning + ' on ' + esc(bd.label) + '.</h3>' +
          '<p class="ppp-card__body">' + bd.days + ' days away.</p></div></div>'
      : '<div class="ppp-card ppp-card--birthday">' +
          '<div class="ppp-card__date ppp-card__date--empty" aria-hidden="true"><b>+</b><span>ADD</span></div>' +
          '<div><p class="ppp-card__k">Missing</p><h3>Add ' + esc(p.pet_name) + "'s birthday</h3>" +
          '<p class="ppp-card__body">It keeps their life stage right on its own, instead of going stale the way a typed-in age does.</p></div></div>';

    var facts = [
      ['Weight', p.weight ? esc(p.weight) + ' kg' : '&mdash;', p.weight ? '' : 'Not set'],
      ['Sex', esc(p.sex || '&mdash;'), p.desexed ? 'Neutered' : 'Not neutered'],
      ['Loves', esc(p.preferred_protein || 'No preference'),
        esc([p.food_form ? p.food_form + ' food' : '', p.likes].filter(Boolean).join(', '))],
      ['Never', (p.avoid_proteins || []).length ? esc(p.avoid_proteins.join(', ')) : 'Nothing yet',
        esc(p.dislikes || '')]
    ].map(function (f) {
      return '<div class="ppp-fact"><dt>' + f[0] + '</dt><dd>' + f[1] +
        (f[2] ? '<small>' + f[2] + '</small>' : '') + '</dd></div>';
    }).join('');

    var chips = facets.map(function (f) {
      return '<button class="ppp-mchip' + (f.avoid ? ' ppp-mchip--avoid' : '') + '" type="button"' +
        ' data-pp-facet="' + esc(f.k) + '" aria-pressed="' + (!off[f.k]) + '">' +
        esc(f.label) + ' <b aria-hidden="true">' + (off[f.k] ? '+' : '&times;') + '</b></button>';
    }).join('');

    var note = '<div class="ppp-card ppp-card--note">' +
        '<p class="ppp-card__k">Vet notes</p>' +
        '<h3>' + (p.needs ? '1 note saved' : 'No notes yet') + '</h3>' +
        '<p class="ppp-card__body">' + (p.needs ? esc(p.needs) : 'Conditions, medication, or anything your vet has said.') + '</p>' +
        '<span class="ppp-lock">' + LOCK + ' Kept on this device only</span>' +
      '</div>';

    /*
      The passport spread. The left page is who the pet is and never
      changes; the right page is a tab - the shelf, or their medicine.

      The due count rides on the TAB, not inside the panel, so an overdue
      treatment is visible while you are looking at the shelf. A reminder
      you have to go and find is not a reminder.
    */
    var due = medsDue(p);
    var badge = due
      ? ' <span class="ppp-tab__badge">' + due + ' due</span>'
      : '';

    var tabs =
      '<div class="ppp-tabs" role="tablist" aria-label="' + esc(p.pet_name) + ' details">' +
        '<button class="ppp-tab is-on" type="button" role="tab" aria-selected="true"' +
          ' data-pp-tab="shelf" id="ppp-tab-shelf" aria-controls="ppp-panel-shelf">Shelf</button>' +
        '<button class="ppp-tab" type="button" role="tab" aria-selected="false"' +
          ' data-pp-tab="meds" id="ppp-tab-meds" aria-controls="ppp-panel-meds">Medicine' + badge + '</button>' +
      '</div>';

    /*
      The facets describe the PET, not the shelf, so they live on the
      passport with everything else about the animal. Collapsed by default:
      a full profile makes fourteen of them, which open is a wall rather
      than a control.
    */
    var matchBlock = facets.length
      ? '<div class="ppp-match">' +
          '<button class="ppp-match__sum" type="button" data-pp-match-toggle aria-expanded="' + matchOpen + '">' +
            '<span class="ppp-match__l">Matching on</span>' +
            '<span class="ppp-match__count">' + on + ' of ' + facets.length + '</span>' +
            '<span class="ppp-match__more" data-pp-match-label>' + (matchOpen ? 'Hide' : 'Change') +
            '<span aria-hidden="true"> &darr;</span></span>' +
          '</button>' +
          '<div class="ppp-match__chips" data-pp-match-chips' + (matchOpen ? '' : ' hidden') + '>' + chips + '</div>' +
        '</div>'
      : '';

    var shelfPanel =
      '<div class="ppp-panel" role="tabpanel" id="ppp-panel-shelf" aria-labelledby="ppp-tab-shelf" data-pp-panel="shelf">' +
        '<section class="ppp-sec" data-pp-shelf-section>' +
          '<div class="ppp-sec__head"><div>' +
            '<p class="ppp-eyebrow">Chosen from ' + esc(p.pet_name) + "'s profile</p></div>" +
            /* The shelf scrolls sideways in this column - it is half the page
               wide now, so four tiles never fit. Arrows because a horizontal
               scrollbar on a trackpad is easy to miss entirely. */
            '<div class="ppp-shelf-nav">' +
              '<button class="ppp-arrow" type="button" data-pp-shelf-prev aria-label="Previous products" disabled>' +
                '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 3 5 8l5 5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
              '</button>' +
              '<button class="ppp-arrow" type="button" data-pp-shelf-next aria-label="Next products" disabled>' +
                '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m6 3 5 5-5 5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
              '</button>' +
            '</div>' +
            (s.all ? '<a class="ppp-chip" href="' + esc(s.all) + '">See all &rarr;</a>' : '') +
          '</div>' +
          '<div class="ppp-shelf" data-pp-shelf aria-live="polite" aria-busy="true">' +
            '<p class="ppp-shelf-note">Building ' + esc(p.pet_name) + "'s shelf&hellip;</p>" +
          '</div>' +
        '</section>' +
      '</div>';

    var medsPanel =
      '<div class="ppp-panel" role="tabpanel" id="ppp-panel-meds" aria-labelledby="ppp-tab-meds"' +
        ' data-pp-panel="meds" hidden>' + medsHTML(p) + '</div>';

    /*
      The birthday is a fact on the passport now, not its own card - "born
      2 Apr 2019, turns 8 in 201 days" says the same thing in the place
      someone looks for a date of birth.
    */
    var born = bd
      ? ['Born', esc(prettyDate(p.birthday)), esc(p.pet_name) + ' turns ' + bd.turning + ' in ' + bd.days + ' days']
      : ['Born', 'Not set', 'It keeps their life stage right on its own'];

    var passportFacts = [
      born,
      ['Weight', p.weight ? esc(p.weight) + ' kg' : 'Not set', ''],
      ['Loves', esc(p.preferred_protein || 'No preference'),
        esc([p.food_form ? p.food_form + ' food' : '', p.likes].filter(Boolean).join(', '))],
      ['Never', (p.avoid_proteins || []).length ? esc(p.avoid_proteins.join(', ')) : 'Nothing yet',
        esc(p.dislikes || '')]
    ].map(function (f) {
      return '<div class="ppp-fact"><dt>' + f[0] + '</dt><dd>' + f[1] +
        (f[2] ? '<small>' + f[2] + '</small>' : '') + '</dd></div>';
    }).join('');

    var vet = '<div class="ppp-vet">' +
      '<p class="ppp-card__k">Vet notes</p>' +
      '<p class="ppp-vet__b">' + (p.needs ? esc(p.needs) : 'Kept on this device only &mdash; never sent to Pet Plus.') + '</p>' +
      '</div>';

    var verify = verificationState(p);
    var verificationUrl = Store.verificationUrl ? Store.verificationUrl(p) : '';
    var verification = '<div class="ppp-verification-summary ppp-verification-summary--' + verify.cls + '">' +
      '<div><span class="ppp-verification-badge">' + esc(verify.label) + '</span>' +
      '<p>' + esc(verify.copy) + '</p></div>' +
      (verificationUrl
        ? '<a href="' + esc(verificationUrl) + '" target="_blank" rel="noopener">View submitted ID</a>'
        : '') +
      '</div>';

    var acts = '<div class="ppp-id__act">' +
      '<button class="ppp-btn ppp-btn--sun" type="button" data-pp-edit="' + esc(p.id) + '">Edit passport</button>' +
      '<button class="ppp-btn ppp-btn--ghost" type="button" data-pp-new>Add a pet</button>' +
      '</div>';

    /*
      Stacked, not side by side. As two columns the grid held both to the
      same height, so the taller side squeezed the shorter one and the
      passport ended up clipping its own vet notes. Full width each, and
      neither has to fit the other.
    */
    return '<section class="ppp-card ppp-card--passport">' +
        '<div class="ppp-strip">' +
          '<span class="ppp-strip__l">Switch pet</span>' +
          '<div class="ppp-switch" data-pp-switch></div>' +
        '</div>' +
        /* Identity on the left, the facts beside it - the card is wide
           enough that stacking them left a long empty stripe to the right
           of the portrait. */
        '<div class="ppp-top">' +
          '<div class="ppp-top__id">' +
            '<p class="ppp-eyebrow ppp-page__k">Pet passport</p>' +
            identityHTML(p, s) +
          '</div>' +
          '<dl class="ppp-facts">' + passportFacts + '</dl>' +
        '</div>' +
        verification + vet + acts + matchBlock +
      '</section>' +
      '<section class="ppp-card ppp-card--panels">' + tabs + shelfPanel + medsPanel + '</section>' +
      orderHTML();
  }

  function orderHTML() {
    if (!ORDERS.length) return '';
    var map = Store.assignmentsSync();
    var cards = ORDERS.map(function (o) {
      var ownerPet = pets.filter(function (p) { return p.id === map[o.id]; })[0];
      var s = ownerPet ? speciesOf(ownerPet) : null;
      var options = ['<option value="">Choose a pet</option>'].concat(pets.map(function (p) {
        return '<option value="' + esc(p.id) + '"' + (ownerPet && ownerPet.id === p.id ? ' selected' : '') +
          '>' + esc(p.pet_name) + '</option>';
      })).join('');
      return '<div class="ppp-acard">' +
        '<div class="ppp-acard__p">' + (o.image ? '<img src="' + esc(o.image) + '" alt="" loading="lazy">' : '') + '</div>' +
        '<div><p class="ppp-acard__t">' +
          (o.url ? '<a href="' + esc(o.url) + '">' + esc(o.title) + '</a>' : esc(o.title)) + '</p>' +
        '<p class="ppp-acard__d">' + esc(o.order) + ' &middot; ' + esc(o.date) +
          (o.price ? ' &middot; ' + esc(o.price) : '') + '</p>' +
        '<span class="ppp-assign' + (ownerPet ? '' : ' ppp-assign--unset') + '">' +
          (s ? '<img src="' + esc(photoFor(ownerPet, s.face)) + '" alt="">' : '') +
          '<select data-pp-assign="' + esc(o.id) + '" aria-label="Who was this for?">' + options + '</select>' +
        '</span></div></div>';
    }).join('');

    return '<section class="ppp-sec">' +
      '<div class="ppp-sec__head"><div><p class="ppp-eyebrow">From your orders</p>' +
      "<h2>You've bought these before</h2>" +
      '<p class="ppp-sec__sub">Tell us who each was for and we\'ll file it against the right pet.</p></div></div>' +
      '<div class="ppp-again">' + cards + '</div></section>';
  }

  /* ---------- the shelf --------------------------------------------- */

  function loadShelf(p, s) {
    var box = detail.querySelector('[data-pp-shelf]');
    if (!box || !s.shelf) return;

    var facets = activeFacets(p);
    var tags = facets.filter(function (f) { return !f.avoid; }).map(function (f) { return f.k; });
    var avoid = facets.filter(function (f) { return f.avoid; }).map(function (f) { return f.k; });

    /* Shopify's tag paths are an AND. Too many tags and the shelf comes
       back empty, which reads as broken rather than specific - so the
       narrowest facets go first and the tail is dropped. */
    var url = '/collections/' + encodeURIComponent(s.shelf);
    if (tags.length) url += '/' + tags.slice(0, 3).map(encodeURIComponent).join('+');
    url += '?view=' + encodeURIComponent(CONFIG.shelfView || 'pet-shelf');

    var mine = ++shelfToken;
    box.classList.add('is-loading');
    box.setAttribute('aria-busy', 'true');

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('shelf ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (mine !== shelfToken) return;   /* a newer pet won the race */
        box.innerHTML = html;
        dropAvoided(box, avoid);
        box.classList.remove('is-loading');
        box.setAttribute('aria-busy', 'false');
        if (!REDUCED) {
          var tiles = box.querySelectorAll('.pp-tile');
          Array.prototype.forEach.call(tiles, function (t, i) {
            t.animate(
              [{ opacity: 0, transform: 'translateY(.8rem)' }, { opacity: 1, transform: 'none' }],
              { duration: 380, delay: i * 45, easing: EASE_IN, fill: 'backwards' }
            );
          });
        }
      })
      .catch(function () {
        if (mine !== shelfToken) return;
        box.classList.remove('is-loading');
        box.setAttribute('aria-busy', 'false');
        box.innerHTML = '<p class="ppp-shelf-note"><b>We could not load the shelf.</b>' +
          'Check your connection and try switching pet again' +
          (s.all ? ', or <a href="' + esc(s.all) + '">browse everything</a>' : '') + '.</p>';
      });
  }

  /* An avoided protein cannot be expressed in a tag URL - that path is an
     include, not an exclude - so the tiles come back and the ones that
     carry the tag are removed here. The shelf therefore asks for a few
     more than it shows. */
  function dropAvoided(box, avoid) {
    if (!avoid.length) return;
    var grid = box.querySelector('[data-pp-shelf-grid]');
    if (!grid) return;
    Array.prototype.forEach.call(grid.querySelectorAll('.pp-tile'), function (tile) {
      var link = tile.querySelector('.pp-tile__title a');
      var name = link ? link.textContent.toLowerCase() : '';
      var hit = avoid.some(function (a) {
        var word = a.split('_').pop().replace(/-/g, ' ');
        return name.indexOf(word) > -1;
      });
      if (hit) tile.remove();
    });
    if (!grid.querySelectorAll('.pp-tile').length) {
      box.innerHTML = '<p class="ppp-shelf-note"><b>Nothing matches all of that.</b>' +
        'Turn one of the chips above off to widen the shelf.</p>';
    }
  }

  /* ---------- switching --------------------------------------------- */

  function switchPet(id) {
    if (id === focusId || swapping) return;
    markActive(id);                     /* answer the tap before anything else */
    if (REDUCED) { focusId = id; off = {}; paint(); return; }
    swapping = true;
    var out = [detail].map(function (el) {
      return el.animate(
        [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(.9rem)' }],
        { duration: 190, easing: EASE_OUT, fill: 'forwards' }
      );
    });
    Promise.all(out.map(function (a) { return a.finished; })).then(function () {
      focusId = id;
      off = {};
      paint();
      out.forEach(function (a) { a.cancel(); });   /* same task, so no flash */
      Array.prototype.forEach.call(detail.children, function (el, i) {
        el.animate(
          [{ opacity: 0, transform: 'translateY(1.1rem)' }, { opacity: 1, transform: 'none' }],
          { duration: 440, delay: 90 + i * 55, easing: EASE_IN, fill: 'backwards' }
        );
      });
      swapping = false;
    });
  }

  /* Toggling a chip only changes the column, so only the column moves. */
  function repaintDetail() {
    var p = current();
    if (!p) return;

    /*
      Toggling a chip rebuilds this whole column, and a rebuilt element
      starts at scrollTop 0 - so switching a filter off while scrolled down
      inside the filter box threw the shopper back to the top of it, mid
      gesture. Remember where they were and put them back.
    */
    var oldChips = detail.querySelector('[data-pp-match-chips]');
    var chipScroll = oldChips ? oldChips.scrollTop : 0;
    var oldRight = detail.querySelector('.ppp-page--r');
    var rightScroll = oldRight ? oldRight.scrollTop : 0;

    detail.innerHTML = detailHTML(p, speciesOf(p));
    renderSwitcher();
    wire();

    var newChips = detail.querySelector('[data-pp-match-chips]');
    if (newChips) newChips.scrollTop = chipScroll;
    var newRight = detail.querySelector('.ppp-page--r');
    if (newRight) newRight.scrollTop = rightScroll;

    loadShelf(p, speciesOf(p));
  }

  /*
    Add a logged treatment to the focused pet and persist it.

    It goes through Store.save like every other change, which means for a
    signed-in shopper it reaches the customer record and for a guest it
    stays in this browser - the same deal as the rest of the profile, with
    no second save path to keep in step.

    Repainting on the medicine tab rather than the shelf: the shopper was
    looking at their medicine when they logged it, and being thrown back to
    the shelf would read as the save having failed.
  */
  function saveTreatment(record) {
    var p = current();
    if (!p) return;

    if (!Array.isArray(p.treatments)) p.treatments = [];
    p.treatments.push({
      id: 't' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
      kind: record.kind,
      date: record.date,
      product: record.product || '',
      interval_days: record.interval_days || 90
    });

    Store.save(pets).then(function (saved) {
      pets = Store.readSync();
      repaintDetail();
      openMedsTab();
      if (!saved) {
        var note = detail.querySelector('.ppp-meds__note');
        if (note) {
          note.textContent = 'Saved on this device only - we could not reach your account.';
          note.classList.add('is-warn');
        }
      }
    }).catch(function () {
      repaintDetail();
      openMedsTab();
    });
  }

  function openMedsTab() {
    var tab = detail.querySelector('[data-pp-tab="meds"]');
    if (tab) tab.click();
  }

  /* ---------- wizard ------------------------------------------------ */

  var STEPS = [
    { step: 'Step 1 of 5', title: 'Let\'s meet your pet', blurb: '' },
    { step: 'Step 2 of 5', title: 'Their body', blurb: 'This is what keeps food size and life stage right as they grow.' },
    { step: 'Step 3 of 5', title: 'What they eat', blurb: 'Pick what matches day to day. You can change any of it later.' },
    { step: 'Step 4 of 5', title: 'Care and sensitivities', blurb: 'Helps us avoid poor matches and prioritise the right support.' },
    { step: 'Step 5 of 5', title: 'Verify their passport', blurb: 'Upload a vaccination record or veterinary report. Pet Plus will review it before approval.' }
  ];
  var STEP_LABELS = ['Basics', 'Body', 'Food', 'Care', 'ID'];
  var GOALS = ['Sensitive Stomach', 'Weight Control', 'Joint Care', 'Dental Care', 'Skin & Coat'];
  var PROTEINS = ['Chicken', 'Beef', 'Lamb', 'Salmon', 'Duck', 'Ostrich', 'Turkey'];

  var scrim = root.querySelector('[data-pp-scrim]');
  var form = root.querySelector('[data-pp-form]');
  var wizBody = root.querySelector('[data-pp-wiz-body]');
  var passportPhoto = root.querySelector('[data-pp-wiz-photo]');
  var photoInput = root.querySelector('[data-pp-photo]');
  var photoChoose = root.querySelector('[data-pp-photo-choose]');
  var photoRemove = root.querySelector('[data-pp-photo-remove]');
  var photoStatus = root.querySelector('[data-pp-photo-status]');
  var saveError = root.querySelector('[data-pp-save-error]');
  var draft = null;
  var stepIdx = 0;
  var lastFocused = null;
  var creatingPet = false;
  var originalPhotoVersion = '';
  var pendingPhotoVersion = '';
  var photoPreviewUrl = '';
  var photoUploadController = null;
  var originalVerificationVersion = '';
  var pendingVerificationVersion = '';
  var verificationUploadController = null;

  function newPetId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return 'pet-' + window.crypto.randomUUID();
    }
    return 'pet-' + Date.now() + '-' + Math.floor(Math.random() * 1000000);
  }

  function clearPhotoPreview() {
    if (photoPreviewUrl) window.URL.revokeObjectURL(photoPreviewUrl);
    photoPreviewUrl = '';
  }

  function passportFallback() {
    var species = draft && SPECIES[draft.pet_type];
    return species ? species.face : (SPECIES.dogs ? SPECIES.dogs.face : '');
  }

  function updatePassportPhoto() {
    if (!draft || !passportPhoto) return;
    passportPhoto.src = photoPreviewUrl || photoFor(draft, passportFallback());
    passportPhoto.alt = draft.pet_name ? draft.pet_name + ' profile photo' : 'Pet profile preview';
    photoRemove.hidden = !draft.photo_version && !photoPreviewUrl;
  }

  function setPhotoStatus(message, isError, isBusy) {
    photoStatus.textContent = message;
    photoStatus.classList.toggle('is-error', Boolean(isError));
    photoChoose.disabled = Boolean(isBusy);
    photoRemove.disabled = Boolean(isBusy);
    photoChoose.textContent = isBusy
      ? 'Preparing photo…'
      : (!CONFIG.loggedIn
        ? 'Sign in to add photo'
        : (draft && draft.photo_version ? 'Change photo' : 'Choose photo'));
  }

  function renderPassportSteps() {
    root.querySelector('[data-pp-wiz-steps]').innerHTML = STEP_LABELS.map(function (label, i) {
      var state = i === stepIdx ? ' is-on' : (i < stepIdx ? ' is-done' : '');
      return '<div class="ppp-passport__step' + state + '"><i>' + (i + 1) + '</i><span>' + label + '</span></div>';
    }).join('');
  }

  function discardPendingPhoto(keepVersion) {
    if (!pendingPhotoVersion || pendingPhotoVersion === keepVersion || !draft) return;
    Store.deletePhoto(draft.id, pendingPhotoVersion);
    pendingPhotoVersion = '';
  }

  function discardPendingVerification(keepVersion) {
    if (!pendingVerificationVersion || pendingVerificationVersion === keepVersion || !draft) return;
    if (Store.deleteVerification) Store.deleteVerification(draft.id, pendingVerificationVersion);
    pendingVerificationVersion = '';
  }

  function openWizard(pet) {
    creatingPet = !pet;
    draft = pet ? JSON.parse(JSON.stringify(pet)) : {
      id: newPetId(), photo_version: '', verification_status: 'required',
      verification_document_version: '', verification_document_name: '',
      verification_submitted_at: '', verification_reviewed_at: '',
      pet_name: '', pet_type: '', breed: '', birthday: '', weight: '',
      pet_size: '', sex: '', desexed: false, preferred_protein: '', food_form: '',
      activity_level: 'Moderate', health_goals: [], avoid_proteins: [],
      likes: '', dislikes: '', needs: ''
    };
    if (!draft.photo_version) draft.photo_version = '';
    if (!draft.verification_status) draft.verification_status = 'required';
    if (!draft.verification_document_version) draft.verification_document_version = '';
    if (!draft.verification_document_name) draft.verification_document_name = '';
    if (!draft.verification_submitted_at) draft.verification_submitted_at = '';
    if (!draft.verification_reviewed_at) draft.verification_reviewed_at = '';
    originalPhotoVersion = draft.photo_version;
    pendingPhotoVersion = '';
    originalVerificationVersion = draft.verification_document_version;
    pendingVerificationVersion = '';
    clearPhotoPreview();
    stepIdx = 0;
    lastFocused = document.activeElement;
    root.querySelector('[data-pp-delete]').hidden = !pet;
    saveError.hidden = true;
    saveError.textContent = '';
    scrim.hidden = false;
    document.body.style.overflow = 'hidden';
    setPhotoStatus(
      CONFIG.loggedIn ? 'Optional · JPG, PNG or WebP' : 'Sign in to save a pet photo',
      false,
      false
    );
    renderStep();
  }

  function closeWizard(saved) {
    if (photoUploadController) photoUploadController.abort();
    photoUploadController = null;
    if (verificationUploadController) verificationUploadController.abort();
    verificationUploadController = null;
    if (!saved) discardPendingPhoto(originalPhotoVersion);
    if (!saved) discardPendingVerification(originalVerificationVersion);
    clearPhotoPreview();
    scrim.hidden = true;
    document.body.style.overflow = '';
    draft = null;
    creatingPet = false;
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function loadPhotoSource(file) {
    function loadWithImageElement() {
      return new Promise(function (resolve, reject) {
        var url = window.URL.createObjectURL(file);
        var image = new Image();
        image.onload = function () {
          resolve({
            source: image,
            width: image.naturalWidth,
            height: image.naturalHeight,
            cleanup: function () { window.URL.revokeObjectURL(url); }
          });
        };
        image.onerror = function () {
          window.URL.revokeObjectURL(url);
          reject(new Error('unreadable_photo'));
        };
        image.src = url;
      });
    }

    if (window.createImageBitmap) {
      return window.createImageBitmap(file, { imageOrientation: 'from-image' }).then(function (bitmap) {
        return {
          source: bitmap,
          width: bitmap.width,
          height: bitmap.height,
          cleanup: function () { bitmap.close(); }
        };
      }).catch(loadWithImageElement);
    }

    return loadWithImageElement();
  }

  function canvasBlob(canvas, type, quality) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (blob) resolve(blob);
        else reject(new Error('photo_processing_failed'));
      }, type, quality);
    });
  }

  function preparePhoto(file) {
    if (!file || ['image/jpeg', 'image/png', 'image/webp'].indexOf(file.type) === -1) {
      return Promise.reject(new Error('unsupported_photo'));
    }
    if (file.size > MAX_SOURCE_PHOTO_BYTES) {
      return Promise.reject(new Error('source_too_large'));
    }

    return loadPhotoSource(file).then(function (loaded) {
      if (!loaded.width || !loaded.height) {
        loaded.cleanup();
        throw new Error('unreadable_photo');
      }

      var canvas = document.createElement('canvas');
      canvas.width = PHOTO_SIZE;
      canvas.height = PHOTO_SIZE;
      var ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) {
        loaded.cleanup();
        throw new Error('photo_processing_failed');
      }

      var sourceSize = Math.min(loaded.width, loaded.height);
      var sourceX = (loaded.width - sourceSize) / 2;
      var sourceY = (loaded.height - sourceSize) / 2;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, PHOTO_SIZE, PHOTO_SIZE);
      ctx.drawImage(
        loaded.source,
        sourceX,
        sourceY,
        sourceSize,
        sourceSize,
        0,
        0,
        PHOTO_SIZE,
        PHOTO_SIZE
      );
      loaded.cleanup();

      return canvasBlob(canvas, 'image/webp', 0.82).then(function (blob) {
        if (blob.type === 'image/webp' && blob.size <= MAX_UPLOAD_PHOTO_BYTES) return blob;
        return canvasBlob(canvas, 'image/jpeg', 0.8);
      }).then(function (blob) {
        if (blob.size > MAX_UPLOAD_PHOTO_BYTES) throw new Error('processed_too_large');
        return blob;
      });
    });
  }

  function photoErrorMessage(error) {
    var code = error && error.message;
    if (code === 'not_logged_in') return 'Sign in before adding a pet photo.';
    if (code === 'unsupported_photo') return 'Choose a JPG, PNG or WebP photo.';
    if (code === 'source_too_large') return 'Choose a photo smaller than 10 MB.';
    if (code === 'processed_too_large') return 'This photo could not be compressed enough. Try another one.';
    if (code === 'unreadable_photo') return 'We could not read that photo. Try another one.';
    return 'We could not upload the photo. Check your connection and try again.';
  }

  function uploadSelectedPhoto(file) {
    if (!CONFIG.loggedIn) {
      setPhotoStatus('Sign in before adding a pet photo.', true, false);
      return;
    }

    if (photoUploadController) photoUploadController.abort();
    photoUploadController = new AbortController();
    var signal = photoUploadController.signal;
    var formerPending = pendingPhotoVersion;
    setPhotoStatus('Preparing photo…', false, true);

    preparePhoto(file)
      .then(function (blob) {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        setPhotoStatus('Uploading photo…', false, true);
        return Store.uploadPhoto(draft.id, blob, signal).then(function (payload) {
          return { blob: blob, payload: payload };
        });
      })
      .then(function (result) {
        if (signal.aborted || !draft) return;
        if (formerPending && formerPending !== originalPhotoVersion) {
          Store.deletePhoto(draft.id, formerPending);
        }
        clearPhotoPreview();
        photoPreviewUrl = window.URL.createObjectURL(result.blob);
        draft.photo_version = result.payload.photo_version;
        pendingPhotoVersion = result.payload.photo_version;
        updatePassportPhoto();
        setPhotoStatus('Photo ready · save this pet to keep it', false, false);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        setPhotoStatus(photoErrorMessage(error), true, false);
      })
      .then(function () {
        if (photoInput) photoInput.value = '';
        photoUploadController = null;
      });
  }

  function verificationErrorMessage(error) {
    var code = error && error.message;
    if (code === 'not_logged_in') return 'Sign in before submitting identification.';
    if (code === 'document_too_large') return 'Choose a file smaller than 5 MB.';
    if (code === 'unsupported_document') return 'Choose a PDF, JPG, PNG or WebP file.';
    return 'We could not upload this document. Check your connection and try again.';
  }

  function setVerificationStatus(message, isError, isBusy) {
    var status = wizBody.querySelector('[data-pp-verification-status]');
    var choose = wizBody.querySelector('[data-pp-verification-choose]');
    var save = root.querySelector('[data-pp-next]');
    if (status) {
      status.textContent = message;
      status.classList.toggle('is-error', Boolean(isError));
    }
    if (choose) {
      choose.disabled = Boolean(isBusy);
      choose.textContent = isBusy
        ? 'Uploading…'
        : (!CONFIG.loggedIn
          ? 'Sign in to add document'
          : (draft && draft.verification_document_version ? 'Replace document' : 'Choose document'));
    }
    if (save && stepIdx === STEPS.length - 1) {
      save.disabled = Boolean(isBusy);
      if (isBusy) save.setAttribute('aria-busy', 'true');
      else save.removeAttribute('aria-busy');
    }
  }

  function uploadSelectedVerification(file) {
    if (!CONFIG.loggedIn) {
      setVerificationStatus('Sign in before submitting identification.', true, false);
      return;
    }
    if (!file || ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].indexOf(file.type) === -1) {
      setVerificationStatus('Choose a PDF, JPG, PNG or WebP file.', true, false);
      return;
    }
    if (file.size > MAX_VERIFICATION_BYTES) {
      setVerificationStatus('Choose a file smaller than 5 MB.', true, false);
      return;
    }

    if (verificationUploadController) verificationUploadController.abort();
    verificationUploadController = new AbortController();
    var signal = verificationUploadController.signal;
    var formerPending = pendingVerificationVersion;
    setVerificationStatus('Uploading securely…', false, true);

    Store.uploadVerification(draft.id, file, signal)
      .then(function (payload) {
        if (signal.aborted || !draft) return;
        if (formerPending && formerPending !== originalVerificationVersion) {
          Store.deleteVerification(draft.id, formerPending);
        }
        draft.verification_status = 'pending';
        draft.verification_document_version = payload.verification_document_version;
        draft.verification_document_name = payload.verification_document_name;
        draft.verification_submitted_at = payload.verification_submitted_at;
        draft.verification_reviewed_at = '';
        pendingVerificationVersion = payload.verification_document_version;
        renderStep();
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        setVerificationStatus(verificationErrorMessage(error), true, false);
      })
      .then(function () {
        var input = wizBody.querySelector('[data-pp-verification]');
        if (input) input.value = '';
        verificationUploadController = null;
      });
  }

  function wireVerificationUpload() {
    var input = wizBody.querySelector('[data-pp-verification]');
    var choose = wizBody.querySelector('[data-pp-verification-choose]');
    if (!input || !choose) return;
    choose.addEventListener('click', function () {
      if (!CONFIG.loggedIn) {
        window.location.assign(CONFIG.loginUrl || '/account/login?return_url=%2Fpages%2Fmy-pets');
        return;
      }
      input.click();
    });
    input.addEventListener('change', function () {
      if (input.files && input.files[0]) uploadSelectedVerification(input.files[0]);
    });
  }

  function collect() {
    wizBody.querySelectorAll('[data-k]').forEach(function (el) {
      var k = el.getAttribute('data-k');
      if (el.type === 'checkbox' && el.hasAttribute('data-multi')) {
        if (!Array.isArray(draft[k])) draft[k] = [];
        var i = draft[k].indexOf(el.value);
        if (el.checked && i === -1) draft[k].push(el.value);
        if (!el.checked && i > -1) draft[k].splice(i, 1);
      } else if (el.type === 'checkbox') {
        draft[k] = el.checked;
      } else if (el.type === 'radio') {
        if (el.checked) draft[k] = el.value;
      } else {
        draft[k] = el.value;
      }
    });
  }

  /* ==================================================================
     Custom dropdown.

     A native <select> cannot be styled inside - the option list is drawn
     by the operating system - so on this wizard it arrived as a grey OS
     control in the middle of a designed panel, different on every
     machine. This is the replacement: a button, a listbox, and a hidden
     input that carries the value.

     The hidden input is the whole trick. `collect()` walks `[data-k]`
     and reads `.value`, so as far as every other part of this file is
     concerned nothing has changed - no new save path, no new parsing.

     Keyboard behaviour is the part people skip, so: Enter, Space and the
     arrow keys open it; arrows move; Home and End jump; Enter or Space
     picks; Escape closes and returns focus to the button; clicking
     outside closes. The list itself takes focus (tabindex="-1") and
     announces the active option with aria-activedescendant, which keeps
     the wizard's focus trap out of it.
     ================================================================== */
  var selUid = 0;

  function sel(values, cur, opts) {
    opts = opts || {};
    var id = 'ppp-sel-' + (++selUid);
    var current = null;

    var items = values.map(function (v) {
      var val = typeof v === 'string' ? v : v.v;
      var lab = typeof v === 'string' ? v : v.l;
      var on = String(cur) === String(val);
      if (on) current = lab;
      return '<li class="ppp-sel__opt" role="option" id="' + id + '-o' + esc(String(val)).replace(/[^a-zA-Z0-9_-]/g, '') +
        '" data-v="' + esc(String(val)) + '" aria-selected="' + (on ? 'true' : 'false') + '">' + esc(lab) + '</li>';
    }).join('');

    if (current === null) current = typeof values[0] === 'string' ? values[0] : values[0].l;

    return '<div class="ppp-sel' + (opts.disabled ? ' is-disabled' : '') + '" data-pp-sel>' +
      '<input type="hidden"' + (opts.k ? ' data-k="' + esc(opts.k) + '"' : '') +
      (opts.role ? ' data-sel-role="' + esc(opts.role) + '"' : '') +
      ' value="' + esc(String(cur == null ? '' : cur)) + '">' +
      '<button type="button" class="ppp-sel__btn" id="' + id + '" aria-haspopup="listbox" aria-expanded="false"' +
      (opts.label ? ' aria-label="' + esc(opts.label) + '"' : '') +
      (opts.disabled ? ' disabled' : '') + '>' +
      '<span class="ppp-sel__value">' + esc(current) + '</span>' +
      '<svg class="ppp-sel__chev" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
      '<path d="m4 6 4 4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '<ul class="ppp-sel__list" role="listbox" tabindex="-1" hidden>' + items + '</ul>' +
      '</div>';
  }

  function wireSelects(scope, onChange) {
    scope.querySelectorAll('[data-pp-sel]').forEach(function (wrap) {
      /* Wire once. The birthday group wires its own three first, with a
         callback; the general pass must not attach a second set of
         handlers on top of them. */
      if (wrap.getAttribute('data-pp-sel-wired')) return;
      wrap.setAttribute('data-pp-sel-wired', '1');

      var button = wrap.querySelector('.ppp-sel__btn');
      var list = wrap.querySelector('.ppp-sel__list');
      var value = wrap.querySelector('input[type="hidden"]');
      var label = wrap.querySelector('.ppp-sel__value');
      var options = Array.prototype.slice.call(wrap.querySelectorAll('.ppp-sel__opt'));
      if (!button || !list || !options.length) return;

      var active = Math.max(0, options.findIndex(function (o) {
        return o.getAttribute('aria-selected') === 'true';
      }));

      function mark(i) {
        active = Math.max(0, Math.min(options.length - 1, i));
        options.forEach(function (o, n) { o.classList.toggle('is-active', n === active); });
        list.setAttribute('aria-activedescendant', options[active].id);
        /* Keep the highlighted row in view when arrowing through a long
           list - the year list is thirty items. */
        var o = options[active];
        if (o.offsetTop < list.scrollTop) list.scrollTop = o.offsetTop;
        else if (o.offsetTop + o.offsetHeight > list.scrollTop + list.clientHeight) {
          list.scrollTop = o.offsetTop + o.offsetHeight - list.clientHeight;
        }
      }

      function open() {
        if (button.disabled) return;
        list.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        mark(active);
        list.focus();
      }

      function close(refocus) {
        list.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        list.removeAttribute('aria-activedescendant');
        if (refocus) button.focus();
      }

      function choose(i) {
        var o = options[i];
        if (!o) return;
        options.forEach(function (other) { other.setAttribute('aria-selected', 'false'); });
        o.setAttribute('aria-selected', 'true');
        value.value = o.getAttribute('data-v');
        label.textContent = o.textContent;
        active = i;
        close(true);
        if (onChange) onChange(wrap, value.value);
      }

      button.addEventListener('click', function () {
        if (list.hidden) open(); else close(true);
      });

      button.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      });

      list.addEventListener('keydown', function (event) {
        switch (event.key) {
          case 'ArrowDown': event.preventDefault(); mark(active + 1); break;
          case 'ArrowUp': event.preventDefault(); mark(active - 1); break;
          case 'Home': event.preventDefault(); mark(0); break;
          case 'End': event.preventDefault(); mark(options.length - 1); break;
          case 'Enter':
          case ' ': event.preventDefault(); choose(active); break;
          case 'Escape': event.preventDefault(); close(true); break;
          case 'Tab': close(false); break;
          default: break;
        }
      });

      options.forEach(function (o, i) {
        o.addEventListener('click', function () { choose(i); });
        o.addEventListener('mousemove', function () { mark(i); });
      });

      list.addEventListener('blur', function () {
        /* A click on an option blurs the list before the click fires, so
           closing immediately would swallow the selection. */
        setTimeout(function () {
          if (!wrap.contains(document.activeElement)) close(false);
        }, 0);
      });
    });
  }

  /* ------------------------------------------------------------------
     Birthday as three dropdowns.

     It was a native date input, which opens a calendar on today's month.
     A pet born in 2011 meant clicking back through a hundred and eighty
     months. Year, month and day as lists gets there in three clicks.

     The value still leaves here as YYYY-MM-DD in a hidden input, exactly
     what the date input produced, so nothing downstream knows or cares.

     Future dates were prevented by the input's `max`; they are prevented
     here structurally instead - the year list stops at this year, and the
     month and day lists shorten when you are in the current year and
     month. A pet cannot be born tomorrow.
     ------------------------------------------------------------------ */
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  function daysIn(year, month) {
    if (!year || !month) return 31;
    return new Date(Number(year), Number(month), 0).getDate();
  }

  function birthdayMarkup(value) {
    var now = new Date();
    var parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    var y = parts ? parts[1] : '';
    var m = parts ? String(Number(parts[2])) : '';
    var d = parts ? String(Number(parts[3])) : '';

    var years = [{ v: '', l: 'Year' }];
    for (var i = now.getFullYear(); i >= now.getFullYear() - 30; i--) {
      years.push({ v: String(i), l: String(i) });
    }

    /*
      Every month and every day, always. The lists are NOT trimmed to what
      is currently valid: a list whose length changes under you as you pick
      is disorienting, and 30 February is better handled by correcting it
      than by hiding it. `compose()` below does the correcting.
    */
    var months = [{ v: '', l: 'Month' }];
    for (var n = 1; n <= 12; n++) months.push({ v: String(n), l: MONTHS[n - 1] });

    var days = [{ v: '', l: 'Day' }];
    for (var k = 1; k <= 31; k++) days.push({ v: String(k), l: String(k) });

    return '<div class="ppp-date" data-pp-date>' +
      '<input type="hidden" data-k="birthday" value="' + esc(value || '') + '">' +
      sel(days, d, { role: 'day', label: 'Day of birth' }) +
      sel(months, m, { role: 'month', label: 'Month of birth' }) +
      sel(years, y, { role: 'year', label: 'Year of birth' }) +
      '</div>';
  }

  function wireBirthday(scope) {
    var box = scope.querySelector('[data-pp-date]');
    if (!box) return;
    var hidden = box.querySelector('input[data-k="birthday"]');

    function field(role) {
      return box.querySelector('[data-sel-role="' + role + '"]');
    }

    function part(role) {
      var el = field(role);
      return el ? el.value : '';
    }

    /* Move a dropdown to a value from code - used only when correcting an
       impossible date, so the control shows what was actually stored. */
    function setPart(role, val) {
      var el = field(role);
      if (!el) return;
      var wrap = el.parentNode;
      var option = wrap.querySelector('.ppp-sel__opt[data-v="' + val + '"]');
      if (!option) return;
      wrap.querySelectorAll('.ppp-sel__opt').forEach(function (o) {
        o.setAttribute('aria-selected', o === option ? 'true' : 'false');
      });
      wrap.querySelector('.ppp-sel__value').textContent = option.textContent;
      el.value = val;
    }

    function compose() {
      var y = part('year');
      var m = part('month');
      var d = part('day');

      /* Partial answers are not a date. Store nothing until all three are
         in, rather than inventing the missing pieces. */
      if (!y || !m || !d) {
        hidden.value = '';
        draft.birthday = '';
        return;
      }

      /* 31 April, or 29 February in a common year: pull it back to the last
         real day of that month and show the correction. */
      var max = daysIn(y, m);
      if (Number(d) > max) {
        d = String(max);
        setPart('day', d);
      }

      var iso = y + '-' + ('0' + m).slice(-2) + '-' + ('0' + d).slice(-2);

      /* The native input had `max` set to today. Keep that guarantee: a pet
         cannot have been born tomorrow. */
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      if (new Date(iso + 'T00:00:00') > today) {
        iso = today.getFullYear() + '-' +
          ('0' + (today.getMonth() + 1)).slice(-2) + '-' +
          ('0' + today.getDate()).slice(-2);
        setPart('year', String(today.getFullYear()));
        setPart('month', String(today.getMonth() + 1));
        setPart('day', String(today.getDate()));
      }

      hidden.value = iso;
      draft.birthday = iso;
    }

    wireSelects(box, compose);
    compose();
  }

  function picks(k, values, cur, cls) {
    return '<div class="ppp-picks ' + (cls || '') + '">' + values.map(function (v) {
      var val = typeof v === 'string' ? v : v.v;
      var lab = typeof v === 'string' ? v : v.l;
      return '<label class="ppp-pick"><input type="radio" name="' + k + '" data-k="' + k + '" value="' +
        esc(val) + '"' + (cur === val ? ' checked' : '') + '><span>' + esc(lab) + '</span></label>';
    }).join('') + '</div>';
  }

  function multi(k, values, cur, cls) {
    cur = Array.isArray(cur) ? cur : [];
    return '<div class="ppp-picks ' + (cls || '') + '">' + values.map(function (v) {
      return '<label class="ppp-pick"><input type="checkbox" data-multi="1" data-k="' + k + '" value="' +
        esc(v) + '"' + (cur.indexOf(v) > -1 ? ' checked' : '') + '><span>' + esc(v) + '</span></label>';
    }).join('') + '</div>';
  }

  function renderStep() {
    var s = STEPS[stepIdx];
    var d = draft;
    root.querySelector('[data-pp-wiz-step]').textContent = s.step;
    root.querySelector('[data-pp-wiz-title]').textContent = s.title;
    root.querySelector('[data-pp-wiz-blurb]').textContent = s.blurb;
    root.querySelector('[data-pp-wiz-blurb]').hidden = !s.blurb;
    renderPassportSteps();
    root.querySelector('[data-pp-wiz-bar]').innerHTML = STEPS.map(function (_, i) {
      return '<span class="ppp-wiz__seg' + (i <= stepIdx ? ' is-on' : '') + '"></span>';
    }).join('');
    root.querySelector('[data-pp-back]').style.visibility = stepIdx === 0 ? 'hidden' : 'visible';
    var saveButton = root.querySelector('[data-pp-next]');
    saveButton.disabled = false;
    saveButton.removeAttribute('aria-busy');
    var finalLabel = pendingVerificationVersion || d.verification_status === 'required' || d.verification_status === 'rejected'
      ? 'Submit for review'
      : 'Save changes';
    saveButton.innerHTML = stepIdx === STEPS.length - 1
      ? finalLabel + ' <span class="ppp-btn__ico" aria-hidden="true">' +
        '<svg class="ppp-ico" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" ' +
        'stroke-linecap="round" stroke-linejoin="round"><path d="M3 8.4 6.4 12 13 4.6"/></svg></span>'
      : 'Continue <span class="ppp-btn__ico" aria-hidden="true">&rarr;</span>';

    if (stepIdx === 0) {
      var options = Object.keys(SPECIES).map(function (key) {
        return '<label class="ppp-pick"><input type="radio" name="pet_type" data-k="pet_type" value="' +
          esc(key) + '"' + (d.pet_type === key ? ' checked' : '') + '>' +
          '<span><img src="' + esc(SPECIES[key].face) + '" alt="">' + esc(SPECIES[key].label) + '</span></label>';
      }).join('');
      wizBody.innerHTML =
        '<label class="ppp-field"><span>Their name</span>' +
        '<input id="ppp-pet-name" data-k="pet_name" value="' + esc(d.pet_name) + '" placeholder="e.g. Milo" autocomplete="off" maxlength="40" aria-describedby="ppp-pet-error"></label>' +
        '<div class="ppp-field"><span>What are they?</span><div class="ppp-species">' + options + '</div></div>' +
        '<label class="ppp-field"><span>Breed <small>Optional</small></span>' +
        '<input data-k="breed" value="' + esc(d.breed) + '" placeholder="e.g. Labrador, tabby, or mixed" autocomplete="off" maxlength="60"></label>' +
        '<p class="ppp-err" id="ppp-pet-error" data-pp-err role="alert" hidden></p>';
    } else if (stepIdx === 1) {
      wizBody.innerHTML =
        '<div class="ppp-row">' +
          '<div class="ppp-field"><span>Birthday <small>Keeps their life stage right</small></span>' +
          birthdayMarkup(d.birthday) + '</div>' +
          '<label class="ppp-field"><span>Weight <small>in kg</small></span>' +
          '<input type="number" step="0.1" min="0" max="200" data-k="weight" value="' + esc(d.weight) + '" placeholder="e.g. 32"></label>' +
        '</div>' +
        '<div class="ppp-field"><span>Size' + (d.pet_type === 'dogs' ? '' : ' <small>Dogs only</small>') + '</span>' +
        sel(
          [{ v: '', l: 'Not applicable' }].concat(['small', 'medium', 'large', 'giant'].map(function (x) {
            return { v: x, l: titled(x) };
          })),
          d.pet_size,
          { k: 'pet_size', label: 'Size', disabled: d.pet_type !== 'dogs' }
        ) + '</div>' +
        '<div class="ppp-field"><span>Sex</span>' + picks('sex', ['Male', 'Female'], d.sex) + '</div>' +
        '<label class="ppp-pick ppp-check"><input type="checkbox" data-k="desexed"' + (d.desexed ? ' checked' : '') +
        '><span>Neutered or spayed</span></label>';
    } else if (stepIdx === 2) {
      wizBody.innerHTML =
        '<div class="ppp-field"><span>Favourite protein</span>' +
        picks('preferred_protein', [{ v: '', l: 'No preference' }].concat(PROTEINS.map(function (x) {
          return { v: x, l: x };
        })), d.preferred_protein) + '</div>' +
        '<div class="ppp-field"><span>Food they prefer</span>' +
        picks('food_form', [{ v: '', l: 'No preference' }, { v: 'Dry', l: 'Dry' }, { v: 'Wet', l: 'Wet' }, { v: 'Pouch', l: 'Pouch' }], d.food_form) + '</div>' +
        '<div class="ppp-field"><span>How active are they?</span>' +
        picks('activity_level', [{ v: 'Low', l: 'Easy-going' }, { v: 'Moderate', l: 'Moderate' }, { v: 'High', l: 'Very active' }], d.activity_level) + '</div>';
    } else if (stepIdx === 3) {
      wizBody.innerHTML =
        '<div class="ppp-field"><span>Health goals <small>Choose any that apply</small></span>' +
        multi('health_goals', GOALS, d.health_goals) + '</div>' +
        '<div class="ppp-field"><span>Proteins to avoid <small>We hide these from the shelf</small></span>' +
        multi('avoid_proteins', ['Chicken', 'Beef', 'Lamb', 'Salmon'], d.avoid_proteins, 'ppp-picks--avoid') + '</div>' +
        '<div class="ppp-row">' +
          '<label class="ppp-field"><span>Things they love <small>Optional</small></span>' +
          '<input data-k="likes" value="' + esc(d.likes) + '" placeholder="e.g. soft treats, fetch toys" maxlength="120"></label>' +
          '<label class="ppp-field"><span>Dislikes or allergies <small>Optional</small></span>' +
          '<input data-k="dislikes" value="' + esc(d.dislikes) + '" placeholder="e.g. grain, loud toys" maxlength="120"></label>' +
        '</div>' +
        '<label class="ppp-field"><span>Vet notes <small>Optional</small></span>' +
        '<textarea data-k="needs" rows="3" maxlength="600" placeholder="Conditions, medication, anything your vet has said">' +
        esc(d.needs) + '</textarea></label>' +
        '<p class="ppp-privacy">' + LOCK +
        '<span>Vet notes stay in this browser. They are not sent to Pet Plus and nobody here can read them.</span></p>';
    } else {
      var verification = verificationState(d);
      var documentUrl = Store.verificationUrl ? Store.verificationUrl(d) : '';
      var verificationMessage = !CONFIG.loggedIn
        ? 'Sign in to add a vaccination record or veterinary report.'
        : (pendingVerificationVersion
          ? 'Uploaded securely · submit this passport to send it for review.'
          : (d.verification_status === 'approved'
            ? 'This identification has been approved.'
            : (d.verification_status === 'pending'
              ? 'Submitted · Pet Plus will review it shortly.'
              : (d.verification_status === 'rejected'
                ? 'This document was not accepted. Upload a replacement to resubmit.'
                : 'A vaccination record or veterinary report is required.'))));

      wizBody.innerHTML =
        '<div class="ppp-verification-upload ppp-verification-upload--' + verification.cls + '">' +
          '<div class="ppp-verification-upload__head">' +
            '<div><span class="ppp-verification-badge">' + esc(verification.label) + '</span>' +
            '<h3>Vaccination record or vet report</h3></div>' +
            '<span class="ppp-verification-lock" aria-hidden="true">' + LOCK + '</span>' +
          '</div>' +
          '<p>Use a clear scan or photo that identifies your pet. Accepted files: PDF, JPG, PNG or WebP, up to 5 MB.</p>' +
          (d.verification_document_name
            ? '<div class="ppp-verification-file"><strong>' + esc(d.verification_document_name) + '</strong>' +
              (documentUrl ? '<a href="' + esc(documentUrl) + '" target="_blank" rel="noopener">View</a>' : '') + '</div>'
            : '') +
          '<input class="ppp-visually-hidden" type="file" tabindex="-1" accept="application/pdf,image/jpeg,image/png,image/webp" data-pp-verification>' +
          '<button class="ppp-btn ppp-btn--blue" type="button" data-pp-verification-choose>' +
            (!CONFIG.loggedIn
              ? 'Sign in to add document'
              : (d.verification_document_version ? 'Replace document' : 'Choose document')) + '</button>' +
          '<p class="ppp-verification-status" data-pp-verification-status role="status">' + esc(verificationMessage) + '</p>' +
        '</div>' +
        '<p class="ppp-privacy">' + LOCK +
        '<span>Your document is private and can only be viewed by you and authorised Pet Plus admins.</span></p>';
    }

    updatePassportPhoto();
    setPhotoStatus(
      CONFIG.loggedIn
        ? (pendingPhotoVersion
          ? 'Photo ready · save this pet to keep it'
          : (d.photo_version ? 'Photo saved with this pet' : 'Optional · JPG, PNG or WebP'))
        : 'Sign in to save a pet photo',
      false,
      false
    );

    if (stepIdx === 0) {
      wizBody.querySelectorAll('input[name="pet_type"]').forEach(function (input) {
        input.addEventListener('change', function () {
          if (!input.checked) return;
          draft.pet_type = input.value;
          if (!draft.photo_version) updatePassportPhoto();
        });
      });
      var nameInput = wizBody.querySelector('[data-k="pet_name"]');
      if (nameInput) {
        nameInput.addEventListener('input', function () {
          draft.pet_name = nameInput.value;
          passportPhoto.alt = nameInput.value ? nameInput.value + ' profile photo' : 'Pet profile preview';
        });
      }
    }

    /*
      The custom dropdowns carry their value on a hidden input, and a hidden
      input cannot take focus - so this has to skip them and reach for the
      dropdown's button instead, or step 2 would open with focus nowhere.
    */
    /* Birthday first: it wires its own three dropdowns WITH the compose
       callback and marks them, so the general pass then leaves them alone. */
    wireBirthday(wizBody);
    wireSelects(wizBody);
    if (stepIdx === STEPS.length - 1) wireVerificationUpload();

    var first = wizBody.querySelector(
      'input:not([type="hidden"]), select, textarea, .ppp-sel__btn:not([disabled])'
    );
    if (first) first.focus();
  }

  function saveDraft() {
    var nextPets = creatingPet
      ? pets.concat([draft])
      : pets.map(function (p) { return p.id === draft.id ? draft : p; });
    var id = draft.id;
    var saveButton = root.querySelector('[data-pp-next]');
    var previousButton = saveButton.innerHTML;
    saveButton.disabled = true;
    saveButton.setAttribute('aria-busy', 'true');
    saveButton.textContent = 'Saving…';
    saveError.hidden = true;

    Store.save(nextPets).then(function (saved) {
      if (!saved) throw new Error('profile_save_failed');
      pets = Store.readSync();
      focusId = id;
      off = {};
      closeWizard(true);
      render();
    }).catch(function () {
      saveButton.disabled = false;
      saveButton.removeAttribute('aria-busy');
      saveButton.innerHTML = previousButton;
      var reason = Store.lastError ? Store.lastError() : '';
      saveError.textContent = reason && reason !== 'profile_save_failed' && reason !== 'network_error'
        ? reason
        : 'We could not save this pet. Check your connection and try again.';
      saveError.hidden = false;
    });
  }

  /* ---------- events ------------------------------------------------ */

  function wire() {
    root.querySelectorAll('[data-pp-edit]').forEach(function (el) {
      el.addEventListener('click', function () {
        var p = pets.filter(function (x) { return x.id === el.getAttribute('data-pp-edit'); })[0];
        if (p) openWizard(p);
      });
    });
    /*
      Shelf arrows. The grid is queried on every use rather than captured
      here, because the shelf arrives from the network after this runs - at
      wire() time there is nothing to scroll yet.
    */
    var shelfBox = detail.querySelector('[data-pp-shelf]');
    var prevBtn = detail.querySelector('[data-pp-shelf-prev]');
    var nextBtn = detail.querySelector('[data-pp-shelf-next]');

    function rail() {
      return shelfBox ? shelfBox.querySelector('.pp-grid') : null;
    }

    function step(r) {
      var tile = r.querySelector('.pp-tile, li');
      /* One tile plus its gap, so a press lands cleanly on the next card
         rather than leaving a sliver of the previous one. */
      return tile ? Math.round(tile.getBoundingClientRect().width) + 20 : Math.round(r.clientWidth * 0.8);
    }

    function syncArrows() {
      var r = rail();
      if (!prevBtn || !nextBtn) return;
      if (!r || r.scrollWidth <= r.clientWidth + 4) {
        /* Nothing to scroll - disabled rather than hidden, so the header
           does not change height when a shelf happens to be short. */
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }
      prevBtn.disabled = r.scrollLeft <= 2;
      nextBtn.disabled = r.scrollLeft + r.clientWidth >= r.scrollWidth - 2;
    }

    function nudge(dir) {
      var r = rail();
      if (!r) return;
      r.scrollBy({ left: dir * step(r), behavior: REDUCED ? 'auto' : 'smooth' });
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { nudge(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { nudge(1); });

    if (shelfBox) {
      shelfBox.addEventListener('scroll', syncArrows, true);
      /* The shelf replaces its own contents when it loads; watch for that
         rather than guessing how long the fetch takes. */
      if (window.MutationObserver) {
        new MutationObserver(syncArrows).observe(shelfBox, { childList: true, subtree: true });
      }
      syncArrows();
    }

    var matchToggle = detail.querySelector('[data-pp-match-toggle]');
    if (matchToggle) {
      matchToggle.addEventListener('click', function () {
        var chipBox = detail.querySelector('[data-pp-match-chips]');
        matchOpen = chipBox.hidden;
        chipBox.hidden = !matchOpen;
        matchToggle.setAttribute('aria-expanded', matchOpen ? 'true' : 'false');
        var label = matchToggle.querySelector('[data-pp-match-label]');
        if (label) {
          label.firstChild.nodeValue = matchOpen ? 'Hide' : 'Change';
        }
      });
    }

    /*
      Toggling a chip updates that chip IN PLACE. It used to rebuild the whole
      column, and even after the scroll position was carried across, the box
      still jumped: the glyph flips between a cross and a plus, which are
      different widths, so fourteen chips re-wrapped, the row count changed,
      and the scroll position clamped to a new maximum. Nothing to rebuild,
      nothing to re-wrap, nothing to clamp.

      Only the shelf actually depends on the change, so only the shelf reloads.
    */
    detail.querySelectorAll('[data-pp-facet]').forEach(function (el) {
      el.addEventListener('click', function () {
        var k = el.getAttribute('data-pp-facet');
        if (off[k]) delete off[k]; else off[k] = true;

        var isOn = !off[k];
        el.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        var glyph = el.querySelector('b');
        if (glyph) glyph.innerHTML = isOn ? '&times;' : '+';

        var pet = current();
        if (!pet) return;

        var facets = facetsFor(pet);
        var on = facets.filter(function (f) { return !off[f.k]; }).length;
        var countEl = detail.querySelector('.ppp-match__count');
        if (countEl) countEl.textContent = on + ' of ' + facets.length;

        loadShelf(pet, speciesOf(pet));
      });
    });
    detail.querySelectorAll('[data-pp-assign]').forEach(function (el) {
      el.addEventListener('change', function () {
        var map = Store.assignmentsSync();
        if (el.value) map[el.getAttribute('data-pp-assign')] = el.value;
        else delete map[el.getAttribute('data-pp-assign')];
        Store.assign(map).then(repaintDetail);
      });
    });
    /* Tabs. Both panels stay in the DOM and are toggled with `hidden`, so
       the shelf keeps whatever it has already fetched when you look at the
       medicine tab and come back. */
    detail.querySelectorAll('[data-pp-tab]').forEach(function (el) {
      el.addEventListener('click', function () {
        var want = el.getAttribute('data-pp-tab');
        detail.querySelectorAll('[data-pp-tab]').forEach(function (t) {
          var on = t === el;
          t.classList.toggle('is-on', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        detail.querySelectorAll('[data-pp-panel]').forEach(function (panel) {
          panel.hidden = panel.getAttribute('data-pp-panel') !== want;
        });
      });
    });

    var medForm = detail.querySelector('[data-pp-med-form]');
    var medSubmit = medForm ? medForm.querySelector('[data-pp-med-submit]') : null;
    var medError = medForm ? medForm.querySelector('[data-pp-med-error]') : null;

    function syncMedForm() {
      if (!medForm || !medSubmit) return;
      var date = medForm.querySelector('[data-pp-med-date]').value;
      var interval = medForm.querySelector('[data-pp-med-interval]').value;
      medSubmit.disabled = !date || !interval;
      if (date && interval && medError) medError.hidden = true;
    }

    function showMedForm(kind) {
      if (!medForm) return;
      medForm.hidden = false;
      if (kind) medForm.querySelector('[data-pp-med-kind]').value = kind;
      var dateInput = medForm.querySelector('[data-pp-med-date]');
      syncMedForm();
      medForm.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'nearest' });
      dateInput.focus();
    }

    detail.querySelectorAll('[data-pp-med-add]').forEach(function (el) {
      el.addEventListener('click', function () {
        showMedForm(el.getAttribute('data-pp-med-add'));
      });
    });

    if (medForm) {
      medForm.addEventListener('input', syncMedForm);
      medForm.addEventListener('change', syncMedForm);
      syncMedForm();

      medForm.querySelector('[data-pp-med-cancel]').addEventListener('click', function () {
        medForm.hidden = true;
        if (medError) medError.hidden = true;
      });

      medForm.addEventListener('submit', function (event) {
        event.preventDefault();
        var date = medForm.querySelector('[data-pp-med-date]').value;
        var interval = Number(medForm.querySelector('[data-pp-med-interval]').value);
        if (!date || !interval) {
          if (medError) medError.hidden = false;
          if (!date) medForm.querySelector('[data-pp-med-date]').focus();
          else medForm.querySelector('[data-pp-med-interval]').focus();
          return;
        }
        saveTreatment({
          kind: medForm.querySelector('[data-pp-med-kind]').value,
          date: date,
          product: medForm.querySelector('[data-pp-med-product]').value.trim(),
          interval_days: interval
        });
      });
    }

    /* "Mark done today" repeats the last dose of that kind - same product,
       same repeat - dated today. It is the one-tap path, and the one people
       will actually use; the form is for anything else. */
    detail.querySelectorAll('[data-pp-med-done]').forEach(function (el) {
      el.addEventListener('click', function () {
        var pet = current();
        var kind = el.getAttribute('data-pp-med-done');
        var last = pet ? lastTreatment(pet, kind) : null;
        el.disabled = true;
        saveTreatment({
          kind: kind,
          date: new Date().toISOString().slice(0, 10),
          product: last ? last.product : '',
          interval_days: last ? last.interval_days : 90
        });
      });
    });

    var scrollBtn = root.querySelector('[data-pp-scroll]');
    if (scrollBtn) {
      scrollBtn.addEventListener('click', function () {
        var target = detail.querySelector('[data-pp-shelf-section]');
        if (target) target.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
      });
    }
  }

  root.querySelectorAll('[data-pp-new]').forEach(function (el) {
    el.addEventListener('click', function () { openWizard(null); });
  });
  root.querySelector('[data-pp-cancel]').addEventListener('click', function () { closeWizard(false); });
  root.querySelector('[data-pp-close]').addEventListener('click', function () { closeWizard(false); });
  scrim.addEventListener('click', function (e) { if (e.target === scrim) closeWizard(false); });
  document.addEventListener('keydown', function (e) {
    if (scrim.hidden) return;
    if (e.key === 'Escape') {
      closeWizard(false);
      return;
    }
    if (e.key !== 'Tab') return;

    var focusable = Array.prototype.filter.call(
      scrim.querySelectorAll('button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])'),
      function (el) { return !el.disabled && el.tabIndex >= 0 && el.offsetParent !== null; }
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
  form.addEventListener('submit', function (e) { e.preventDefault(); });

  photoChoose.addEventListener('click', function () {
    if (!CONFIG.loggedIn) {
      setPhotoStatus('Sign in before adding a pet photo.', true, false);
      return;
    }
    photoInput.click();
  });
  photoInput.addEventListener('change', function () {
    if (photoInput.files && photoInput.files[0]) uploadSelectedPhoto(photoInput.files[0]);
  });
  photoRemove.addEventListener('click', function () {
    if (!draft) return;
    if (pendingPhotoVersion && pendingPhotoVersion !== originalPhotoVersion) {
      Store.deletePhoto(draft.id, pendingPhotoVersion);
    }
    pendingPhotoVersion = '';
    draft.photo_version = '';
    clearPhotoPreview();
    updatePassportPhoto();
    setPhotoStatus('Photo removed · save this pet to confirm', false, false);
  });

  root.querySelector('[data-pp-back]').addEventListener('click', function () {
    collect();
    if (stepIdx > 0) { stepIdx--; renderStep(); }
  });

  root.querySelector('[data-pp-delete]').addEventListener('click', function () {
    var id = draft && draft.id;
    if (!id) return;
    var orphanVersion = pendingPhotoVersion && pendingPhotoVersion !== originalPhotoVersion
      ? pendingPhotoVersion
      : '';
    pets = pets.filter(function (p) { return p.id !== id; });
    if (focusId === id) focusId = pets.length ? pets[0].id : null;
    Store.save(pets).then(function () {
      if (orphanVersion) Store.deletePhoto(id, orphanVersion);
      closeWizard(true);
      render();
    });
  });

  root.querySelector('[data-pp-next]').addEventListener('click', function () {
    collect();
    if (stepIdx === 0) {
      var err = wizBody.querySelector('[data-pp-err]');
      var problem = '';
      if (!String(draft.pet_name || '').trim()) problem = 'Give them a name, so we know who the shelf is for.';
      else if (!draft.pet_type) problem = 'Pick what kind of animal they are - it decides which shelf they get.';
      if (problem) {
        if (err) { err.textContent = problem; err.hidden = false; }
        return;
      }
      if (err) err.hidden = true;
    }
    if (stepIdx < STEPS.length - 1) { stepIdx++; renderStep(); return; }
    if (!draft.verification_document_version) {
      setVerificationStatus('Upload a vaccination record or veterinary report before submitting this passport.', true, false);
      return;
    }
    if (draft.verification_status === 'rejected' && !pendingVerificationVersion) {
      setVerificationStatus('Upload a replacement document before resubmitting this passport.', true, false);
      return;
    }
    saveDraft();
  });

  /* ---------- start -------------------------------------------------- */

  window.addEventListener('petplus:pets-migrated', function () {
    Store.read().then(function (list) {
      pets = list;
      if (pets.length) focusId = pets[0].id;
      render();
    });
  });

  Store.read().then(function (list) {
    pets = list;
    if (pets.length) focusId = pets[0].id;
    render();
  });
})();
